import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ButtonCustom } from "../../../components/custom/button-custom";
import ConfirmModal from "../../../components/custom/confirm-modal";
import { showToast } from "../../../components/custom/custom-toast";
import DropdownSelectOne from "../../../components/custom/dropdown-select-one";
import { ModalCustom } from "../../../components/custom/modal-custom";
import { SelectDropdown } from "../../../components/custom/select-dropdown";
import { PRIVILEGE_CODES } from "../../../constants/privileges";
import { STATUS } from "../../../constants/status";
import { useFetchData } from "../../../hooks/useFetch";
import { Attendance } from "../../../models/response/attendance";
import { Branch } from "../../../models/response/branch";
import { Shift } from "../../../models/response/shift";
import { BaseResponsePagination } from "../../../models/response/track-asia/base-response-pagination";
import { MODULE_ROUTE } from "../../../routers/module-router";
import { API_ATTENDANCES_ROUTER, API_SHIFT_ROUTER, ApiConfig } from "../../../services/api-config";
import { useLoading } from "../../../state/loading-context";
import { IRootState } from "../../../store";
import CookieUtils from "../../../utils/cookie-utils";
import { usePrivilegeCodes } from "../../../utils/helpers";
import logger from "../../../utils/logger";
import { convertDateFormat } from "../../../utils/time-utils";

export const TimeKeepingUserButtons = () => {
  const privilegeCodes = usePrivilegeCodes();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const dataBranches = useSelector((state: IRootState) => state.branch.branches);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBranchesModal, setShowBranchesModal] = useState(false);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftPage, setShiftPage] = useState<number>(1);
  const [hasMoreShift, setHasMoreShift] = useState<boolean>(false);
  const [shiftKeySearch, setShiftKeySearch] = useState("");
  const [selectedShift, setSelectedShift] = useState<Shift>(new Shift());
  const [currentShift, setCurrentShift] = useState<Attendance>(new Attendance());
  const { request: checkInApi, baseResponse: checkInRes } = useFetchData.Post<Attendance>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading: true,
  });
  const { request: checkOutApi, baseResponse: checkOutRes } = useFetchData.Post<Attendance>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading: true,
  });
  const { request: getShifts, baseResponse: shiftsRes } = useFetchData.Get<BaseResponsePagination<Shift>>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
  });
  const { request: getCurrentShift, baseResponse: currentShiftRes } = useFetchData.Get<Attendance>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
  });

  useEffect(() => {
    const branchIds = CookieUtils.getCurrentUser().branch_ids;
    const currentBranchId = CookieUtils.getCookie("branch_id") ?? (branchIds && branchIds.length > 0 ? branchIds[0] : "");
    if (dataBranches.length > 0 && currentBranchId) {
      const fetchData = async () => {
        const params = {
          id: currentBranchId,
        };
        await getCurrentShift({
          endPoint: API_ATTENDANCES_ROUTER.API_GET_ATTENDANCE_CURRENT(currentBranchId),
          data: params,
        });
      };

      fetchData();
    }
  }, [dataBranches]);

  useEffect(() => {
    if (!currentShiftRes) return;
    if (currentShiftRes?.data && currentShiftRes?.status === 200) {
      setCurrentShift(currentShiftRes.data);
      CookieUtils.setCookie("branch_id", JSON.stringify(currentShiftRes.data.branch_id));
    }
  }, [currentShiftRes]);

  useEffect(() => {
    setSelectedBranchId("");
    setSelectedShift({ ...new Shift(), _id: "", name: "Chọn lịch làm việc" });
    setShiftKeySearch("");
  }, [showBranchesModal]);

  useEffect(() => {
    setShiftPage(1);
    setHasMoreShift(false);
  }, [shiftKeySearch, selectedBranchId]);

  useEffect(() => {
    if (selectedBranchId) {
      const fetchData = async () => {
        const params = {
          page: shiftPage,
          limit: 10,
          status: STATUS.ACTIVE,
          key_search: shiftKeySearch,
          branch_id: selectedBranchId,
        };
        await getShifts({
          endPoint: API_SHIFT_ROUTER.API_GET_SHIFTS_EMPLOYEE,
          data: params,
        });
      };

      fetchData();
    }
  }, [shiftPage, shiftKeySearch, selectedBranchId]);

  useEffect(() => {
    if (!shiftsRes) return;
    if (shiftsRes?.data && shiftsRes?.status === 200) {
      if (shiftPage === 1) {
        setShifts(shiftsRes.data.list);
      } else {
        setShifts((prev) => [...prev, ...shiftsRes.data.list]);
      }
      setHasMoreShift([...shifts, ...shiftsRes.data.list].length < shiftsRes.data.total_record);
    } else {
      showToast({
        message: shiftsRes?.message,
        type: "error",
      });
    }
  }, [shiftsRes]);

  const fetchLocation = async (isCheckIn: boolean) => {
    setLoading(true);
    try {
      // if (!navigator.geolocation) {
      //   showToast({
      //     message: "Trình duyệt của bạn không hỗ trợ tính năng định vị.",
      //     type: "error",
      //   });
      //   setLoading(false);
      //   throw new Error("Geolocation is not supported by this browser.");
      // }

      // const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

      // if (permissionStatus.state === 'denied') {
      //   showToast({
      //     message: "Quyền truy cập vị trí bị từ chối. Hãy bật quyền này trong cài đặt trình duyệt.",
      //     type: "warning",
      //   });
      //   setLoading(false);
      //   throw new Error("Location permission denied.");
      // }

      // const currentLocation: Coordinate = await getCurrentLocation();

      // if (isCheckIn) {
      //   checkIn(currentLocation.latitude, currentLocation.longitude);
      // } else {
      //   checkOut(currentLocation.latitude, currentLocation.longitude);
      // }

      //Skip Location
      if (isCheckIn) {
        checkIn(0, 0);
      } else {
        checkOut(0, 0);
      }
    } catch (err) {
      logger.error(err);
    }
  };

  const checkIn = async (lat: number, lng: number) => {
    setShowConfirmModal(false);
    const body = {
      branch_id: selectedBranchId,
      shift_id: selectedShift._id,
      lat,
      lng,
    };

    await checkInApi({
      endPoint: API_ATTENDANCES_ROUTER.API_POST_CHECK_IN(),
      data: body,
    });
  };

  useEffect(() => {
    if (!checkInRes) return;
    if (checkInRes?.status === 200) {
      setCurrentShift(checkInRes.data);
      CookieUtils.setCookie("branch_id", JSON.stringify(checkInRes.data.branch_id));
      showToast({
        message: "Check in thành công",
        type: "success",
      });
      if (privilegeCodes.find((item) => item === PRIVILEGE_CODES.ATTENDANCES_BASIC)) {
        navigate(MODULE_ROUTE.TIMEKEEPING_USER_PAGE, { state: { newAttendance: checkInRes.data } });
      } else {
        navigate(MODULE_ROUTE.DASHBOARD);
      }
    } else {
      showToast({
        message: checkInRes?.message,
        type: "error",
      });
    }
    setLoading(false);
  }, [checkInRes]);

  const checkOut = async (lat: number, lng: number) => {
    setShowConfirmModal(false);
    const branchIds = CookieUtils.getCurrentUser().branch_ids;
    const currentBranchId = CookieUtils.getCookie("branch_id") ?? (branchIds && branchIds.length > 0 ? branchIds[0] : "");
    if (!currentBranchId) return;

    const body = {
      branch_id: currentBranchId,
      lat,
      lng,
    };

    await checkOutApi({
      endPoint: API_ATTENDANCES_ROUTER.API_POST_CHECK_OUT(),
      data: body,
    });
  };

  useEffect(() => {
    if (!checkOutRes) return;
    if (checkOutRes?.status === 200) {
      setCurrentShift(new Attendance());
      showToast({
        message: "Check out thành công",
        type: "success",
      });
      navigate(MODULE_ROUTE.TIMEKEEPING_USER_PAGE, { state: { newAttendance: checkOutRes.data } });
    } else {
      showToast({
        message: checkOutRes?.message ?? "Check out thất bại",
        type: "error",
      });
    }
    setLoading(false);
  }, [checkOutRes]);

  const handleCheckIn = () => {
    setShowBranchesModal(true);
  };

  const handleCheckOut = () => {
    setShowConfirmModal(true);
  };

  const handleCheckConfirm = () => {
    setShowBranchesModal(false);
    fetchLocation(true);
  };

  const footerModal = () => {
    return (
      <>
        <ButtonCustom
          type="button"
          text="Hủy bỏ"
          onClick={() => {
            setShowBranchesModal(false);
          }}
          className="text-gray-500 bg-gray-100 hover:bg-gray-400 hover:text-white"
        />
        <ButtonCustom
          disabled={!selectedBranchId || !selectedShift._id}
          type="button"
          text="Đồng ý"
          onClick={handleCheckConfirm}
        />
      </>
    );
  };
  const bodyModal = () => {
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex-1">
          <SelectDropdown
            selectedId={selectedBranchId}
            placeholder={"Chọn chi nhánh làm việc"}
            dataOptions={[{ ...new Branch(), _id: "", name: "Chọn chi nhánh" }, ...dataBranches]}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option._id}
            onChange={(selectedOptions) => {
              let selectedOption = selectedOptions[0];
              setShifts([]);
              setSelectedBranchId(selectedOption ? selectedOption._id : "");
              setSelectedShift({ ...new Shift(), _id: "", name: "Chọn lịch làm việc" });
            }}
          />
        </div>

        <div className="flex-1">
          <DropdownSelectOne
            disabled={!selectedBranchId}
            defaultOption={
              selectedBranchId !== ""
                ? {
                    ...new Shift(),
                    _id: selectedShift._id,
                    name: selectedShift.name,
                    start_time: selectedShift.start_time,
                    end_time: selectedShift.end_time,
                  }
                : { ...new Shift(), _id: "", name: "Chọn lịch làm việc" }
            }
            selectedId={selectedShift._id}
            options={shifts.filter((shift) => shift.is_have_check_in === 0)}
            onChange={(selectedOption: Shift) => {
              setSelectedShift(selectedOption);
            }}
            placeholder={"Chọn lịch làm việc"}
            getOptionLabel={(option) =>
              `${option.name} ${option._id !== "" ? `(${option.start_time} - ${option.end_time})` : ""}`
            }
            getOptionKey={(option) => option._id}
            currentPage={shiftPage}
            hasMore={hasMoreShift}
            onPageChange={setShiftPage}
            handleFilterChange={setShiftKeySearch}
            timeDelay={300}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <ButtonCustom
        disabled={!!currentShift.check_in_time}
        text={`${
          currentShift.check_in_time !== ""
            ? convertDateFormat(currentShift.check_in_time, "dd/MM/yyyy HH:mm", "HH:mm")
            : "Checkin"
        }`}
        className="hidden bg-green-200 text-success hover:bg-success hover:text-white lg:flex"
        onClick={handleCheckIn}
      />
      <ButtonCustom
        disabled={!!currentShift.check_out_time || !currentShift.check_in_time}
        text={`${
          currentShift.check_out_time !== ""
            ? convertDateFormat(currentShift.check_out_time, "dd/MM/yyyy HH:mm", "HH:mm")
            : "Checkout"
        }`}
        className="bg-[#FEEED6] text-[#E96012] hover:bg-[#E96012] hover:text-white hidden lg:flex"
        onClick={handleCheckOut}
      />

      <ModalCustom
        modalSize="lg"
        isOpen={showBranchesModal}
        setIsOpen={() => {
          setShowBranchesModal(false);
        }}
        titleModal={"Chọn lịch làm việc"}
        footerModal={footerModal()}
        bodyModal={bodyModal()}
      />

      <ConfirmModal
        title={`Xác nhận Check out`}
        description={`Hãy chắc chắn là bạn đã hết giờ làm việc!`}
        isOpen={showConfirmModal}
        setIsOpen={setShowConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
        }}
        onConfirm={() => {
          fetchLocation(false);
        }}
      />
    </>
  );
};
